import { Guid } from "@/Common/Models/Guid";
import { dispatch } from "@/Flowsheets/Hooks/useDispatch";
import { useSelector } from "@/Flowsheets/Hooks/useSelector";
import { saveFlowsheetStoreAction, setForceReloadStoreAction } from "../Actions/Actions";
import { Flowsheet } from "../Models/Flowsheet";
import { FlowsheetTemplate } from "../Models/FlowsheetTemplate";

/**
 * Interface representing the result of the useFlowsheetStore hook.
 */
interface IUseFlowsheetStoreResult {
    /**
     * Retrieves a flowsheet by its ID.
     * @param flowsheetId - The ID of the flowsheet to retrieve.
     * @returns The flowsheet with the specified ID, or undefined if not found.
     */
    getFlowsheet: (flowsheetId: Guid) => Flowsheet | null;

    /**
     * Saves a flowsheet for the given patient.
     * @param flowsheet - The flowsheet to save.
     * @param flowsheetTemplate - The template associated with the flowsheet.
     */
    saveFlowsheet: (flowsheet: Flowsheet, flowsheetTemplate?: FlowsheetTemplate) => void;

    /**
     * Retrieves the encounter date.
     */
    getEncounterDate: (flowsheetId: Guid) => Date | null;

    /**
     * Retrieves the patient contact ID.
     */
    patientContactId: Guid;

    /**
     * Checks if a flowsheet should be reloaded.
     * @param flowsheetId - The ID of the flowsheet to check.
     * @returns True if the flowsheet should be reloaded, false otherwise.
     */
    shouldReloadFlowsheet: (flowsheetId: Guid) => boolean;
}

/**
 * Custom hook to manage flowsheet store operations for a given patient.
 * @param patientId - The ContactFacetId of the patient.
 * @returns An object containing functions to manage flowsheets .
 */
function useFlowsheetStore(patientId: string): IUseFlowsheetStoreResult {
    const patientContactId = useSelector(s => s.patient.resolvePatientContactId(patientId)) as Guid;
    const encounters = useSelector(s => s.patient.PatientMedicalSummaryModels[patientContactId]?.get("Encounters") ?? Immutable.List([]));
    const flowsheetsByPatient = useSelector(s => s.patient.FlowsheetModels[patientContactId]);

    const saveFlowsheet = (flowsheet: Flowsheet, flowsheetTemplate?: FlowsheetTemplate): void => {
        dispatch(saveFlowsheetStoreAction(patientId, flowsheet, flowsheetTemplate));
    };

    const getFlowsheet = (flowsheetId: Guid): Flowsheet | null => {
        if (!flowsheetsByPatient)
            return null;

        for (const flowsheet of flowsheetsByPatient) {
            if (flowsheet && flowsheet.get("flowsheetId") === flowsheetId) {
                return flowsheet.get("flowsheet") ?? null;
            }
        }

        return null;
    };

    const shouldReloadFlowsheet = (flowsheetId: Guid): boolean => {
        if (!flowsheetsByPatient)
            return null;

        const flowsheet = flowsheetsByPatient.find(p => p?.get("flowsheetId") === flowsheetId);
        const forceReload = flowsheet?.get("forceReload") ?? false;

        if (forceReload) {
            dispatch(setForceReloadStoreAction(patientContactId, false));
        }

        return forceReload;
    };

    const getEncounterDate = (flowsheetId: Guid): Date | null => {
        if (!flowsheetsByPatient)
            return null;

        const flowsheetModel = flowsheetsByPatient.find(p => p.get("flowsheetId") === flowsheetId);

        const encounterId = flowsheetModel?.get("flowsheetSummary")?.EncounterId ?? flowsheetModel?.get("flowsheet")?.EncounterId;

        if (!encounterId)
            return null;

        return encounters.find(p => p.Id === encounterId)?.ConsultationDate ?? null;
    };

    return {
        getFlowsheet,
        saveFlowsheet,
        getEncounterDate,
        shouldReloadFlowsheet,
        patientContactId
    };
}

export default useFlowsheetStore;
